'use client';

import { useEffect, useRef, useCallback } from 'react';
import type { BriefingModalContent, SignalTone } from '@/lib/types';

interface BriefingModalProps {
  isOpen: boolean;
  content: BriefingModalContent | null;
  onClose: () => void;
}

const TONE_CLASSES: Record<SignalTone, string> = {
  up: 'text-accent',
  down: 'text-error',
  warn: 'text-warning',
  neutral: 'text-foreground',
};

/**
 * CIN-style briefing modal overlay.
 * ~700px centered scrollable panel, dark overlay, no route change.
 * Matches canideal.org/cid-modals.js design pattern.
 */
export default function BriefingModal({ isOpen, content, onClose }: BriefingModalProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Focus trap: focus panel on open
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen || !content) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[9998] flex items-start justify-center bg-black/75 backdrop-blur-sm pt-12 px-4 pb-12 overflow-y-auto animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-label={content.title}
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full max-w-[720px] bg-surface border border-border rounded-[10px] text-foreground shadow-2xl animate-in slide-in-from-bottom-2 duration-200 outline-none max-h-[calc(100vh-96px)] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-6 pt-[18px] pb-[14px] border-b border-border flex items-start justify-between gap-3 bg-gradient-to-b from-primary/5 to-transparent">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-[7px] h-[7px] rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] font-bold tracking-[1.6px] text-primary uppercase">
                {content.tag}
              </span>
            </div>
            <h2 className="text-[18px] font-bold text-foreground leading-[1.35] m-0">
              {content.title}
            </h2>
            <p className="text-[11px] text-muted-foreground mt-1.5 tracking-[0.3px]">
              {content.date}
              {content.byline && (
                <> &middot; <strong className="text-muted-foreground/80 font-semibold">{content.byline}</strong></>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-none w-[30px] h-[30px] rounded-full border border-border bg-background text-muted-foreground text-[18px] flex items-center justify-center hover:text-foreground hover:border-primary transition-all cursor-pointer"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-1">
          {/* Summary */}
          <div className="text-[13px] leading-[1.6] text-muted-foreground mb-[18px] py-[14px] px-4 bg-primary/[0.06] border-l-2 border-primary rounded-r-[4px]">
            {content.body}
          </div>

          {/* Signals grid */}
          {content.signals && content.signals.length > 0 && (
            <>
              <div className="text-[9px] font-bold tracking-[1.4px] text-primary uppercase mt-[18px] mb-2">
                Market Signals
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5 mb-1.5">
                {content.signals.map((sig, i) => (
                  <div
                    key={i}
                    className="bg-background border border-border rounded-[5px] py-3 px-3.5"
                  >
                    <div className="text-[9px] tracking-[1.2px] text-muted-foreground uppercase mb-1">
                      {sig.label}
                    </div>
                    <div className={`text-[13px] font-bold ${TONE_CLASSES[sig.tone ?? 'neutral']}`}>
                      {sig.value}
                    </div>
                    {sig.sub && (
                      <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                        {sig.sub}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Actions list */}
          {content.actions && content.actions.length > 0 && (
            <>
              <div className="text-[9px] font-bold tracking-[1.4px] text-primary uppercase mt-[18px] mb-2">
                Recommended Actions
              </div>
              <ul className="list-none p-0 m-0 mb-2">
                {content.actions.map((action, i) => (
                  <li
                    key={i}
                    className="relative pl-[22px] py-[7px] text-[13px] text-muted-foreground leading-[1.5] border-b border-border/50 last:border-0 before:content-[''] before:absolute before:left-0.5 before:top-[14px] before:w-2 before:h-2 before:border-l-[1.5px] before:border-b-[1.5px] before:border-primary before:-rotate-45"
                  >
                    {action.text}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* CID Take */}
          {content.cidTake && (
            <div className="mt-[18px] p-[14px_16px] bg-accent/[0.06] border border-accent/25 rounded-[6px]">
              <div className="text-[10px] font-bold tracking-[1.4px] text-accent uppercase mb-2">
                CID Take
              </div>
              <p className="text-[12px] text-muted-foreground leading-[1.55] my-1">
                <strong className="text-accent font-semibold mr-1">WHAT:</strong>
                {content.cidTake.what}
              </p>
              <p className="text-[12px] text-muted-foreground leading-[1.55] my-1">
                <strong className="text-accent font-semibold mr-1">WHY:</strong>
                {content.cidTake.why}
              </p>
              <p className="text-[12px] text-muted-foreground leading-[1.55] my-1">
                <strong className="text-accent font-semibold mr-1">NEXT:</strong>
                {content.cidTake.next}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex flex-wrap gap-2 justify-end border-t border-border bg-background/40 sticky bottom-0">
          {content.secondaryCta && (
            <a
              href={content.secondaryHref ?? '#'}
              className="inline-flex items-center gap-1.5 px-4 py-[9px] rounded-[4px] text-[12px] font-semibold text-muted-foreground border border-border bg-transparent hover:text-foreground hover:border-primary transition-all tracking-[0.3px]"
              {...(content.secondaryHref?.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {content.secondaryCta}
            </a>
          )}
          {content.primaryCta && (
            <a
              href={content.primaryHref ?? '#'}
              className="inline-flex items-center gap-1.5 px-4 py-[9px] rounded-[4px] text-[12px] font-semibold bg-primary text-primary-foreground border border-primary hover:bg-secondary transition-all tracking-[0.3px]"
              {...(content.primaryHref?.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {content.primaryCta}
            </a>
          )}
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-4 py-[9px] rounded-[4px] text-[12px] font-semibold text-muted-foreground border border-border bg-transparent hover:text-foreground hover:border-primary transition-all tracking-[0.3px] cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
